// src/app/services/subscription.service.ts
import { Injectable } from '@angular/core';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { FlightConnectionsService } from './flight-connections-services';

// Temporary flag to allow an open-ended trial experience while we encourage usage.
// Set to false to reinstate the standard trial checks (time window + engagement).
const OPEN_ENDED_TRIAL = true;

export interface SubscriptionInfo {
  isSubscribed: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: Date | null;
  currentPeriodEnd: Date | null;
  status?: string | null;
}

export type SubscriptionEntitlementStatus = 'active' | 'trial' | 'inactive';

export interface SubscriptionEntitlement {
  userId: string;
  status: SubscriptionEntitlementStatus;
  renewalDate: Date | null;
  reason:
    | 'subscription-active'
    | 'subscription-canceled'
    | 'trial-active'
    | 'trial-expired'
    | 'connection-engaged'
    | 'free-access-granted'
    | 'no-profile';
  computedAt: Date;
  freeAccessSource?: 'profile' | null;
}

interface SubscriptionSnapshot {
  isSubscribed: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodStartMs: number | null;
  currentPeriodEndMs: number | null;
}

interface FreeAccessGrant {
  granted: boolean;
  cacheKey: string;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private auth = getAuth();
  private db = getFirestore();
  private subscriptionCache: { userId: string; value: SubscriptionInfo } | null = null;
  private entitlementCache: {
    userId: string;
    subscriptionSnapshot: SubscriptionSnapshot;
    createdAtMs: number | null;
    freeAccessGrantKey: string;
    value: SubscriptionEntitlement;
  } | null = null;
  private entitlementInFlight: Promise<SubscriptionEntitlement> | null = null;
  constructor(private connectionsService: FlightConnectionsService) { }

  /**
   * Returns the user’s subscription state and dates.
   */
  async getSubscriptionInfo(forceRefresh = false): Promise<SubscriptionInfo> {
    const user = this.auth.currentUser;
    if (!user) {
      this.clearCache();
      return this.emptySubscription();
    }

    if (!forceRefresh && this.subscriptionCache?.userId === user.uid) {
      return this.subscriptionCache.value;
    }

    const subsRef = collection(this.db, 'users', user.uid, 'subscriptions');
    const now = Timestamp.now();
    const q = query(
      subsRef,
      where('status', '==', 'active'),
      where('currentPeriodEnd', '>', now)
    );

    const snap = await getDocs(q);
    if (snap.empty) {
      const empty = this.emptySubscription();
      this.subscriptionCache = { userId: user.uid, value: empty };
      return empty;
    }

    // If multiple active, pick the one with the furthest end date
    let best: { end: Timestamp; start: Timestamp; status: string | null; cancelAtPeriodEnd: boolean } | null = null;
    snap.forEach(doc => {
      const data = doc.data();
      const canceled = data.cancelAtPeriodEnd === true;
      const end: Timestamp | undefined = data.currentPeriodEnd;
      const start: Timestamp | undefined = data.currentPeriodStart;
      const status = typeof data.status === 'string' ? data.status : null;
      if (end instanceof Timestamp) {
        if (!best || end.toMillis() > best.end.toMillis()) {
          best = {
            end,
            start: start instanceof Timestamp ? start : Timestamp.fromMillis(0),
            status,
            cancelAtPeriodEnd: canceled
          };
        }
      }
    });

    const result: SubscriptionInfo = {
      isSubscribed: true,
      cancelAtPeriodEnd: best?.cancelAtPeriodEnd ?? false,
      currentPeriodStart: best?.start.toDate() ?? null,
      currentPeriodEnd: best?.end.toDate() ?? null,
      status: best?.status ?? null
    };

    this.subscriptionCache = { userId: user.uid, value: result };
    this.entitlementCache = null; // ensure entitlement recomputes with latest subscription
    return result;
  }

  async getEntitlementState(
    profile: any,
    options: { subscription?: SubscriptionInfo; forceRefresh?: boolean } = {}
  ): Promise<SubscriptionEntitlement> {
    const user = this.auth.currentUser;
    const userId = typeof profile?.id === 'string' ? profile.id : user?.uid;

    if (!userId) {
      return {
        userId: '',
        status: 'inactive',
        renewalDate: null,
        reason: 'no-profile',
        computedAt: new Date()
      };
    }

    const subscription = options.subscription ?? (await this.getSubscriptionInfo(options.forceRefresh));
    const createdAtDate = this.asDate(profile?.createdAt);
    const createdAtMs = createdAtDate?.getTime() ?? null;

    const freeAccess = await this.resolveFreeAccessGrant(profile);

    const normalizedSubscription = this.toSnapshot(subscription);
    const cached = this.entitlementCache;
    if (
      !options.forceRefresh &&
      cached &&
      cached.userId === userId &&
      this.equalSnapshots(normalizedSubscription, cached.subscriptionSnapshot) &&
      cached.createdAtMs === createdAtMs &&
      cached.freeAccessGrantKey === freeAccess.cacheKey
    ) {
      return cached.value;
    }

    if (!options.forceRefresh && this.entitlementInFlight) {
      return this.entitlementInFlight;
    }

    const computePromise = this.computeEntitlement(
      userId,
      createdAtDate,
      normalizedSubscription,
      subscription,
      freeAccess
    );
    if (!options.forceRefresh) {
      this.entitlementInFlight = computePromise
        .finally(() => {
          this.entitlementInFlight = null;
        });
    }

    return computePromise;
  }

  clearCache() {
    this.subscriptionCache = null;
    this.entitlementCache = null;
    this.entitlementInFlight = null;
  }

  private async computeEntitlement(
    userId: string,
    createdAt: Date | null,
    subscriptionSnapshot: SubscriptionSnapshot,
    subscription: SubscriptionInfo,
    freeAccess: FreeAccessGrant
  ): Promise<SubscriptionEntitlement> {
    let value: SubscriptionEntitlement;

    if (subscriptionSnapshot.isSubscribed) {
      value = {
        userId,
        status: 'active',
        renewalDate: subscription.currentPeriodEnd ?? null,
        reason: subscription.cancelAtPeriodEnd ? 'subscription-canceled' : 'subscription-active',
        computedAt: new Date(),
        freeAccessSource: null
      };
    } else if (freeAccess.granted) {
      value = {
        userId,
        status: 'active',
        renewalDate: null,
        reason: 'free-access-granted',
        computedAt: new Date(),
        freeAccessSource: 'profile'
      };
    } else if (OPEN_ENDED_TRIAL) {
      value = {
        userId,
        status: 'trial',
        renewalDate: null,
        reason: 'trial-active',
        computedAt: new Date(),
        freeAccessSource: null
      };
    } else {
      const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
      const withinTrialWindow = createdAt ? (Date.now() - createdAt.getTime()) < ninetyDaysMs : false;

      if (withinTrialWindow) {
        const hasEngagedConnection = await this.connectionsService.hasConnectionWithMinMessages(userId, 2);
        if (!hasEngagedConnection) {
          value = {
            userId,
            status: 'trial',
            renewalDate: createdAt ? new Date(createdAt.getTime() + ninetyDaysMs) : null,
            reason: 'trial-active',
            computedAt: new Date(),
            freeAccessSource: null
          };
        } else {
          value = {
            userId,
            status: 'inactive',
            renewalDate: null,
            reason: 'connection-engaged',
            computedAt: new Date(),
            freeAccessSource: null
          };
        }
      } else {
        value = {
          userId,
          status: 'inactive',
          renewalDate: null,
          reason: 'trial-expired',
          computedAt: new Date(),
          freeAccessSource: null
        };
      }
    }

    this.entitlementCache = {
      userId,
      subscriptionSnapshot,
      createdAtMs: createdAt?.getTime() ?? null,
      freeAccessGrantKey: freeAccess.cacheKey,
      value
    };

    return value;
  }

  private async resolveFreeAccessGrant(profile: any): Promise<FreeAccessGrant> {
    const granted = !!profile && typeof profile === 'object' && profile.t1Elite === true;
    return {
      granted,
      cacheKey: granted ? 'profile:t1Elite:true' : 'profile:t1Elite:false'
    };
  }

  private emptySubscription(): SubscriptionInfo {
    return { isSubscribed: false, cancelAtPeriodEnd: false, currentPeriodStart: null, currentPeriodEnd: null, status: null };
  }

  private asDate(value: any): Date | null {
    if (!value) return null;
    if (typeof value?.toDate === 'function') {
      try { return value.toDate(); } catch { return null; }
    }

    if (typeof value === 'object' && typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }

    if (typeof value === 'number') {
      return new Date(value < 1e12 ? value * 1000 : value);
    }

    if (typeof value === 'string') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    if (value instanceof Date) return value;
    return null;
  }

  private toSnapshot(info: SubscriptionInfo | null | undefined): SubscriptionSnapshot {
    return {
      isSubscribed: info?.isSubscribed === true,
      cancelAtPeriodEnd: info?.cancelAtPeriodEnd === true,
      currentPeriodEndMs: info?.currentPeriodEnd instanceof Date ? info.currentPeriodEnd.getTime() : null,
      currentPeriodStartMs: info?.currentPeriodStart instanceof Date ? info.currentPeriodStart.getTime() : null
    };
  }

  private equalSnapshots(a: SubscriptionSnapshot, b: SubscriptionSnapshot): boolean {
    return (
      a.isSubscribed === b.isSubscribed &&
      a.cancelAtPeriodEnd === b.cancelAtPeriodEnd &&
      a.currentPeriodEndMs === b.currentPeriodEndMs &&
      a.currentPeriodStartMs === b.currentPeriodStartMs
    );
  }
}
